/**
 * 外部SA への手動メール作成モーダル。
 *
 * - 宛先: SA のメールアドレスを自動転記 (読取専用)。
 * - 本文: 冒頭「{SA名} 様」+ フッター (会社情報) を自動転記。中央は自由入力。
 * - 送信: sendSaMail Callable (会社アドレスから送信、ログ記録あり)。
 */
import type { MailCompanySettings, MailFooterSettings } from '@goenhub/shared';
import { Mail } from 'lucide-react';
import { useEffect, useState } from 'react';


import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { useMailCompany, useMailFooter } from '@/hooks/useMailSettings';
import { useSendSaMail, type SaAgentRecord } from '@/hooks/useSaAgents';
import { useGeneralSettings } from '@/hooks/useSettings';

function separatorLine(style: string | undefined): string {
  if (style === 'double') return '════════════════════════';
  if (style === 'dots') return '……………………………………………';
  return '────────────────────────';
}

/** 会社情報フッターをクライアント側で組み立てる (functions/companyFooter と同フォーマット)。 */
function buildFooterText(
  company: Partial<MailCompanySettings> | null | undefined,
  footer: Partial<MailFooterSettings> | null | undefined,
): string {
  const c = company ?? {};
  const f = footer ?? {};
  const lines: string[] = [separatorLine(f.separator)];
  if (c.name) lines.push(c.name);
  if (c.postalCode || c.address) {
    lines.push(`〒${c.postalCode ?? ''} ${c.address ?? ''}`.trim());
  }
  if (c.phone) lines.push(`電話: ${c.phone}`);
  if (c.email) lines.push(`Email: ${c.email}`);
  if (c.representative) lines.push(`代表者: ${c.representative}`);
  if (f.webUrl) lines.push(`Web: ${f.webUrl}`);
  if (f.snsLinks) lines.push(`SNS: ${f.snsLinks}`);
  return lines.join('\n');
}

interface SaMailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agent: SaAgentRecord | null;
}

export function SaMailDialog({ open, onOpenChange, agent }: SaMailDialogProps) {
  // v3.3.x: 会社情報は settings/general.companyInfo を優先、無ければ旧 mail_settings/company。
  const { data: general } = useGeneralSettings();
  const { data: legacyCompany } = useMailCompany();
  const company = general?.companyInfo ?? legacyCompany;
  const { data: footer } = useMailFooter();
  const sendMutation = useSendSaMail();

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const saName = agent
    ? agent.fullName || agent.displayName || agent.email
    : '';

  // モーダルを開いた時に宛先・冒頭・フッターを自動転記
  useEffect(() => {
    if (!open || !agent) return;
    const footerText = buildFooterText(company, footer);
    setSubject('');
    setBody(`${saName} 様\n\n\n\n${footerText}`);
  }, [open, agent, company, footer, saName]);

  const handleSend = async () => {
    if (!agent) return;
    if (!subject.trim()) {
      toast.error('件名を入力してください');
      return;
    }
    try {
      const res = await sendMutation.mutateAsync({
        uid: agent.uid,
        subject: subject.trim(),
        body,
      });
      toast.success(`${res.to} にメールを送信しました`);
      onOpenChange(false);
    } catch (err) {
      toast.error(
        `送信に失敗しました: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            <Mail className="inline size-4 mr-1" />
            SA へメール作成
          </DialogTitle>
          <DialogDescription>
            会社アドレスから送信します。フッターの会社情報はメール設定から自動転記されます。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="sa-mail-to">宛先</Label>
            <Input id="sa-mail-to" value={agent?.email ?? ''} readOnly disabled />
          </div>
          <div>
            <Label htmlFor="sa-mail-subject">件名</Label>
            <Input
              id="sa-mail-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="件名を入力"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="sa-mail-body">本文</Label>
            <Textarea
              id="sa-mail-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="min-h-64 font-mono text-xs"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={sendMutation.isPending}
          >
            キャンセル
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSend}
            disabled={sendMutation.isPending || !agent}
          >
            <Mail className="size-4 mr-1" />
            {sendMutation.isPending ? '送信中...' : '送信'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
